// Sun shader, physics-based solar surface simulation.

import { OKLAB_MIX_GLSL } from './oklab.js';

// Ashima 3D simplex noise (MIT), shared by the surface and the prominences.
const SIMPLEX_NOISE_GLSL = `
  // Simplex noise functions
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = mod289(i);
    vec4 p = permute(permute(permute(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }
`;

// The Sun's far field: the plasma, the texture, the mesoscale mottling and
// the facular activity, a function of place and a slow clock only.
// src/sunfarfield.js bakes it into a cube map a band at a time
// (sunFarFieldBakeFragmentShader) and the photosphere shader reads it back
// with one lookup, where it used to cost about 80 noise calls per pixel; it
// is evaluated directly only until the first bake lands. Returns the colour
// divided by SUN_FAR_SCALE, so it fits the cube's unit range, and the
// facular activity in alpha.
//
// The clock runs at SUN_FAR_RATE, much slower than the old 0.008: the
// triple domain warp amplifies time at its fine octaves, so at the old rate
// the fine plasma boiled through in a third of a second and read as
// shimmer, and bakes 8 s apart could not follow it. The cells' shape
// changes over minutes here; what carries them along visibly is the churn
// the photosphere shader puts on the lookup (SUN_CHURN_RATE).
export const SUN_FAR_RATE = 0.0004;

// The surface clock runs SUN_SURFACE_PACE times the wall clock, like the
// time-lapse films the real Sun is known from (SST, DKIST, Hinode run at
// about 10 to 60x): at true pace a granule lives eight minutes and nothing
// on the disc visibly moves. Granulation, the penumbral flows and the
// umbral dots run on it; the spot groups' lives run on the simulation's
// date instead (uSunDays), so a date always shows the same spots.
export const SUN_SURFACE_PACE = 10;

// How fast the far field's churn evolves, per wall second: two octaves of
// a smooth displacement of the lookup, at most about 0.03 radians, which
// moves the plasma's cells at about 0.005 radians a second, so they drift,
// stretch and fold continuously between bakes.
export const SUN_CHURN_RATE = 0.11;

const SUN_FAR_FIELD_GLSL = `
  const float SUN_FAR_SCALE = 1.25;

  float fBm(vec3 p, int octaves) {
    float value = 0.0;
    float amplitude = 0.5;
    float frequency = 1.0;
    for (int i = 0; i < 10; i++) {
      if (i >= octaves) break;
      value += amplitude * snoise(p * frequency);
      frequency *= 2.0;
      amplitude *= 0.5;
    }
    return value;
  }

  float turbulence(vec3 p, int octaves) {
    float value = 0.0;
    float amplitude = 0.5;
    float frequency = 1.0;
    for (int i = 0; i < 8; i++) {
      if (i >= octaves) break;
      value += amplitude * abs(snoise(p * frequency));
      frequency *= 2.0;
      amplitude *= 0.5;
    }
    return value;
  }

  // The texture's address for a direction, as SphereGeometry lays it out.
  vec2 sunTextureUv(vec3 n) {
    float u = atan(n.z, -n.x) / 6.28318530718;
    return vec2(fract(u), 1.0 - acos(clamp(n.y, -1.0, 1.0)) / 3.14159265359);
  }

  vec4 sunFarField(vec3 n, float t, float texLod) {
    vec3 pos = n * 2.25;

    // Multi-scale plasma convection: supergranular cells, then medium and
    // fine flow, each warped by the one before (Quilez).
    vec3 q = vec3(
      fBm(pos + vec3(0.0, 0.0, t), 6),
      fBm(pos + vec3(5.2, 1.3, t * 0.8), 6),
      fBm(pos + vec3(1.7, 9.2, t * 1.2), 6)
    );
    vec3 r = vec3(
      fBm(pos + 3.0 * q + vec3(1.7, 9.2, t * 0.5), 6),
      fBm(pos + 3.0 * q + vec3(8.3, 2.8, t * 0.7), 6),
      fBm(pos + 3.0 * q + vec3(2.5, 3.1, t * 0.9), 6)
    );
    vec3 s = vec3(
      fBm(pos * 2.0 + 2.0 * r + vec3(3.1, 7.4, t * 0.4), 5),
      fBm(pos * 2.0 + 2.0 * r + vec3(6.2, 4.3, t * 0.6), 5),
      fBm(pos * 2.0 + 2.0 * r + vec3(1.8, 8.7, t * 0.5), 5)
    );
    float plasma = fBm(pos + 2.5 * r + 1.5 * s, 7);
    float turbulentDetail = turbulence(pos * 3.0 + vec3(t * 0.3), 6) * 0.25;

    vec4 texColor = textureLod(sunTexture, sunTextureUv(n), texLod);

    // The Detailed palette: warm, orange in the cooler regions, golden
    // across the middle, near-white only at the brightest cores, mixed in
    // OKLCh so each band stays vivid (Bujack et al. 2025, Ottosson 2020).
    vec3 hotCore   = vec3(1.00, 0.97, 0.86);
    vec3 warm      = vec3(1.00, 0.86, 0.55);
    vec3 cool      = vec3(1.00, 0.66, 0.28);
    vec3 deepCool  = vec3(0.92, 0.42, 0.12);
    vec3 ember     = vec3(0.72, 0.24, 0.05);
    float nn = plasma * 0.5 + 0.5 + turbulentDetail;
    vec3 plasmaColor = oklchMix(ember, deepCool, smoothstep(0.0, 0.20, nn));
    plasmaColor = oklchMix(plasmaColor, cool, smoothstep(0.20, 0.50, nn));
    plasmaColor = oklchMix(plasmaColor, warm, smoothstep(0.50, 0.80, nn));
    plasmaColor = oklchMix(plasmaColor, hotCore, smoothstep(0.80, 1.05, nn));
    vec3 baseColor = oklchMix(texColor.rgb, plasmaColor, 0.55);

    // Cooler lanes between the plasma cells.
    float cellBoundary = fBm(pos * 5.0 + vec3(t * 0.2), 5);
    baseColor *= 0.78 + smoothstep(-0.15, 0.15, cellBoundary) * 0.28;

    // Supergranulation brightness, and mottling at mesogranular scales.
    float superGran = snoise(pos * 1.15 + vec3(t * 0.10, t * 0.07, -t * 0.08)) * 0.5 + 0.5;
    float gFine = snoise(pos * 35.0 + vec3(t * 2.0));
    float mottle = (abs(gFine) * 1.45 - 0.50) * 0.07
                 + snoise(pos * 15.0 + vec3(t)) * 0.06
                 + snoise(pos * 5.0 + vec3(t * 0.3)) * 0.04;
    baseColor *= (0.975 + 0.05 * superGran) * (0.94 + mottle * (0.78 + 0.44 * superGran));

    // Facular activity: plage patches, shown near the limb by the caller.
    float activity = snoise(n * 2.0 + vec3(t * 0.3, 0.0, 0.0)) * 0.5 + 0.5;
    float fineActivity = snoise(n * 8.0 + vec3(t * 0.5, t * 0.3, 0.0)) * 0.5 + 0.5;
    activity = smoothstep(0.5, 0.8, activity) * smoothstep(0.3, 0.7, fineActivity);

    return vec4(baseColor / SUN_FAR_SCALE, activity);
  }
`;

// Granulation, the photosphere up close: convection cells about 1,300 km
// across (0.0019 solar radii) with bright domed interiors and dark lanes a
// few hundred km wide, as DKIST, SST and Hinode show it. Worley F2 - F1 on
// the cells of a jittered lattice, sliced by the sphere, which gives the
// spread of sizes a real field has; the walls are curved a little by a
// low-frequency warp. Every lattice site hosts one granule after another,
// each living GRANULE_LIFE on the surface clock: it is born as a small
// bright cell pushing between its neighbours, swells to full size, and
// fades and shrinks back into the lanes as they take its place; the larger
// ones darken in the middle first, as exploding granules do. The site's
// next granule is born somewhere else in it, so the pattern never repeats.
// That is a power diagram (each site's squared distance carries a
// penalty that falls as its granule grows), so birth and death are smooth.
// The magnetic network, supergranule edges about 0.04 radii
// apart, carries bright filigree in the lanes and faculae at the limb.
// The lanes and the network fade to their means once a granule spans
// fewer than about four pixels, so nothing shimmers at range.
const SUN_GRANULATION_GLSL = `
  const float GRANULE_SIZE = 0.0017;
  const float GRANULE_LIFE = 480.0;
  // Mean of the resolved field, so the fade to the unresolved grain keeps
  // the brightness.
  const float GRANULE_MEAN = 0.92;

  uvec3 sunPcg3(uvec3 v) {
    v = v * 1664525u + 1013904223u;
    v.x += v.y * v.z;
    v.y += v.z * v.x;
    v.z += v.x * v.y;
    v ^= v >> 16u;
    v.x += v.y * v.z;
    v.y += v.z * v.x;
    v.z += v.x * v.y;
    return v;
  }
  vec3 sunHash3(vec3 c) {
    return vec3(sunPcg3(uvec3(ivec3(c + 4096.0)))) * (1.0 / 4294967295.0);
  }

  struct SunGranulation { float intensity; float resolved; float network; float plage; };

  SunGranulation sunGranulation(vec3 n, float seconds, float footprint) {
    SunGranulation g;
    float cellsPerPixel = footprint / GRANULE_SIZE;
    g.resolved = 1.0 - smoothstep(0.3, 0.65, cellsPerPixel);
    float net = abs(snoise(n * 26.0 + vec3(0.0, 0.0, seconds * 0.00003)));
    g.network = (1.0 - smoothstep(0.015, 0.06, net)) * (1.0 - smoothstep(0.0015, 0.004, footprint));
    // Faculae toward the limb: bright patches where the network gathers,
    // a few hundredths of a radius across, fading to their mean once a
    // pixel spans a good part of one (no lines to alias at the limb).
    float plage = smoothstep(0.15, 0.6, snoise(n * 38.0 + vec3(11.0, 0.0, seconds * 0.00003)));
    g.plage = mix(0.2, plage, 1.0 - smoothstep(0.003, 0.008, footprint));

    // Unresolved granulation: what a pixel holding several granules sees
    // is their average, whose scatter falls as one over the number across
    // (15% rms for one granule). Three octaves at 2, 4 and 8 granules,
    // each kept while it spans more than about three pixels.
    float grain = 0.0;
    if (g.resolved < 1.0) {
      for (int j = 1; j <= 3; j++) {
        float span = exp2(float(j));
        float keep = 1.0 - smoothstep(0.3, 0.6, footprint / (GRANULE_SIZE * span));
        // The average of many granules changes as they are born and die,
        // more slowly the more granules it spans.
        grain += keep * (0.54 / span) * snoise(n / (GRANULE_SIZE * span) + vec3(float(j) * 7.3, 0.0, seconds * (0.6 / GRANULE_LIFE) / sqrt(span)));
      }
    }
    g.intensity = 1.0 + grain;
    if (g.resolved <= 0.0) return g;

    vec3 p = n / GRANULE_SIZE;
    // Each term finer than the cell fades out once it runs under a few
    // pixels: kN keeps a pattern of N cycles per granule.
    float k2 = 1.0 - smoothstep(0.1, 0.25, 2.0 * cellsPerPixel);
    float k4 = 1.0 - smoothstep(0.1, 0.25, 4.0 * cellsPerPixel);
    // Irregular cells: a broad warp strong enough to stretch some cells
    // into ovals and squeeze others, and a finer one that bends the walls.
    p += 0.62 * vec3(snoise(p * 0.22), snoise(p * 0.22 + 17.1), snoise(p * 0.22 - 9.3));
    p += 0.2 * vec3(snoise(p * 0.9 + 5.3), snoise(p * 0.9 - 11.7), snoise(p * 0.9 + 23.9));
    p += 0.06 * k2 * vec3(snoise(p * 2.1 - 2.9), snoise(p * 2.1 + 8.1), snoise(p * 2.1 + 15.3));
    float tt = seconds * (6.28318530718 / GRANULE_LIFE);
    float lives = seconds / GRANULE_LIFE;
    vec3 ip = floor(p);
    vec3 fp = p - ip;
    float f1 = 9.0;
    float f2 = 9.0;
    float f3 = 9.0;
    vec3 h1 = vec3(0.0);
    float ph1 = 0.5;
    float d1 = 1.0;
    for (int z = -1; z <= 1; z++) {
      for (int y = -1; y <= 1; y++) {
        for (int x = -1; x <= 1; x++) {
          vec3 c = vec3(float(x), float(y), float(z));
          vec3 site = ip + c;
          // This site's granule: which one it is and how far through its
          // life (0 at birth, 1 at death), the sites out of step.
          float age = lives + sunHash3(site + 71.0).x;
          float gen = floor(age);
          float ph = age - gen;
          vec3 h = sunHash3(site + vec3(gen * 61.0, -gen * 17.0, gen * 29.0));
          // Born anywhere in the site, drifting a little as it grows.
          vec3 o = 0.15 + 0.7 * h + 0.1 * (h.yzx - 0.5) * ph;
          vec3 d = c + o - fp;
          // Size through life: grows over the first quarter, holds, and
          // shrinks away over the last 40%; a mature one keeps a little
          // of the penalty at random, so sizes still differ.
          float grow = smoothstep(0.0, 0.25, ph) * (1.0 - smoothstep(0.6, 1.0, ph));
          float euclid = dot(d, d);
          float dd = euclid + 1.1 * (1.0 - grow) + 0.15 * h.z * grow;
          if (dd < f1) {
            f3 = f2;
            f2 = f1;
            f1 = dd;
            h1 = h;
            ph1 = ph;
            d1 = euclid;
          } else if (dd < f2) {
            f3 = f2;
            f2 = dd;
          } else if (dd < f3) {
            f3 = dd;
          }
        }
      }
    }
    f1 = sqrt(f1);
    f2 = sqrt(f2);
    f3 = sqrt(f3);
    float vertex = f3 - f1;   // small where three meet
    float aa = cellsPerPixel;
    // The wall wanders and reaches into the granules as dark tendrils.
    float tendril = snoise(p * 2.6 + vec3(0.0, tt * 0.25, 1.7));
    float edge = f2 - f1 + 0.05 * k2 * tendril;   // 0 on a wall between two granules
    // Lanes swell and pinch along their length, from a hairline to a
    // broad band, and pool at the vertices.
    float swell = snoise(p * 2.2 + vec3(0.0, tt * 0.2, 3.1));
    float pool = 1.0 - smoothstep(0.0, 0.3, vertex);
    float laneW = max(0.022 + 0.022 * k2 * swell + 0.06 * pool, 0.006);
    // Their depth varies too: some segments barely darker than the cells.
    float depth = clamp(0.18 + 0.18 * snoise(p * 0.8 + vec3(7.7, 0.0, tt * 0.15)) + 0.1 * pool, 0.02, 0.4);
    float lane = smoothstep(0.0, laneW + aa, edge);
    // A lane narrower than a pixel is drawn a pixel wide at the darkness
    // it averages to over that pixel, so it neither vanishes nor crawls.
    depth *= laneW / (laneW + aa);
    // The granule rises from the lane over the outer third, then
    // plateaus: no step at its edge.
    float rise = smoothstep(0.0, 0.34, edge);
    // Brightness is a continuous field over the granules, not a flat fill
    // per cell: bright patches and dimmer stretches a granule or half one
    // across, plus a modest offset per granule and its life cycle.
    // Young granules are the brightest (the hottest upflow) and fade as
    // they age, averaging what the old per-cell flicker did.
    float lifeBright = 1.015 - 0.14 * smoothstep(0.4, 1.0, ph1);
    float cellBright = (0.94 + 0.12 * h1.x) * lifeBright;
    float patchA = snoise(p * 0.75 + vec3(h1.x * 0.6, 0.0, tt * 0.35));
    float patchB = snoise(p * 1.9 + vec3(4.4, tt * 0.5, 0.0));
    float meso = snoise(n * 150.0 + vec3(3.7, 1.1, seconds * 0.0004));
    // Exploding granules: the big ones darken in the middle late in life,
    // before they break up.
    float explode = smoothstep(0.7, 0.85, h1.y) * smoothstep(0.4, 0.7, ph1) * (1.0 - smoothstep(0.85, 1.0, ph1));
    float hollow = explode * exp(-d1 / 0.09) * 0.26;
    // Some granules carry a brighter rim just inside the lane.
    float rimMask = smoothstep(0.55, 0.8, h1.z);
    float rim = rimMask * k4 * smoothstep(0.03, 0.1, edge) * (1.0 - smoothstep(0.12, 0.26, edge));
    float interior = cellBright * (0.86 + 0.14 * rise + 0.08 * rim - hollow)
                   * (1.0 + 0.095 * patchA + 0.045 * k2 * patchB + 0.04 * meso);
    float intensity = interior * mix(1.0 - depth, 1.0, lane);
    // Bright points: specks in the lanes where three cells meet, at most
    // junctions of the network and a few elsewhere.
    float speck = 1.0 - smoothstep(0.0, 0.07 + aa, vertex);
    float points = max(g.network, step(0.85, sunHash3(floor(p * 1.5) + 31.0).x));
    intensity += 0.32 * k4 * points * speck * (1.0 - lane);
    g.intensity = mix(g.intensity, intensity / GRANULE_MEAN, g.resolved);
    return g;
  }

  // Granule colour by brightness, read through the ACES curve: orange
  // lanes, orange-gold granules, pale gold at the brightest points, over
  // the white-light contrast (lanes about 70% of a granule), the warm
  // Detailed palette.
  vec3 sunGranuleColour(float intensity) {
    vec3 c = oklchMix(vec3(0.28, 0.088, 0.008), vec3(0.42, 0.14, 0.012), smoothstep(0.65, 0.85, intensity));
    c = oklchMix(c, vec3(0.59, 0.20, 0.016), smoothstep(0.85, 1.0, intensity));
    c = oklchMix(c, vec3(0.86, 0.31, 0.030), smoothstep(1.0, 1.15, intensity));
    c = oklchMix(c, vec3(1.3, 0.55, 0.060), smoothstep(1.15, 1.35, intensity));
    return c;
  }
`;

// Sunspots, as white-light imagery shows them (SDO/HMI on the disc, SST,
// DKIST and Hinode up close). Active regions emerge in SUN_REGION_SLOTS
// slots, each hosting one region after another on the simulation's date:
// a region grows over its first few days, then its area decays linearly
// (Gnevyshev-Waldmeier) over weeks, the follower spots well before the
// leader. Its latitude and how many slots are in use follow the solar
// cycle (Spörer's law and the cycle's rise and slow decline, Cycle 25 from
// its December 2019 minimum), it is tilted by Joy's law with the leader
// nearer the equator, and it drifts against the mesh with differential
// rotation (Snodgrass and Ulrich 1990 against the Carrington rate).
//
// A group is a leader, a follower and a few small spots and pores. Each
// spot is a dark, red-brown umbra (about 0.1 to 0.2 of the photosphere,
// with umbral dots and, in big spots, a light bridge) inside a penumbra of
// radial filaments (bright filaments with dark cores, bright grains at
// their inner heads drifting in, the pattern streaming out with the
// Evershed flow) whose ends fray the outer edge. A spot grows its
// penumbra round its rim as it grows, and small spots stay pores. Faculae
// gather round each group and show toward the limb. Every structure finer
// than a few pixels fades to its mean.
const SUN_SPOTS_GLSL = `
  // Days from J2000 to the Cycle 25 minimum (December 2019), and the mean
  // cycle length.
  const float SUN_CYCLE_START = 7280.0;
  const float SUN_CYCLE_DAYS = 4018.0;
  const int SUN_REGION_SLOTS = 8;
  const float SUN_DEG = 0.0174532925;
  // The Carrington rate the mesh turns at, degrees a day.
  const float SUN_MESH_RATE = 14.18;

  struct SunSpots { float shade; float umbra; float cover; float plage; float flare; };

  // The Sun as it is today (src/spaceweather/viewer.js): NOAA's numbered
  // active regions, placed by their Carrington longitudes, replace the
  // modelled groups while uLiveSun is on. Per region: A the centre (unit
  // vector in the mesh's frame) and the leader's umbra radius; B the
  // leader-follower separation (radians), Joy's tilt, the leader's and the
  // follower's penumbra fractions (the follower's < 0: no follower; the
  // leader's radius 0: plage only); C the follower's umbra radius, the
  // small spots, a seed and a flare's brightening (0 to about 1.5).
  // uLivePole is the Sun's rotation axis in the same frame.
  const int SUN_LIVE_MAX = 12;
  // Umbra core and penumbra intensity against the photosphere, as they go
  // into the shading. The modelled groups keep their approved 0.09 and 0.70.
  // NOAA's regions take uLiveTone (src/spaceweather/viewer.js), set so the
  // displayed contrast is white light's near HMI's 617 nm: a big umbra 0.2
  // to 0.3 of the photosphere (its rim up to 0.12 brighter, pores lighter),
  // the penumbra about 0.8 (Solanki 2003, A&ARv 11, 153).
  const vec2 SUN_SPOT_TONE = vec2(0.09, 0.70);
  uniform vec2 uLiveTone;
  uniform float uLiveSun;
  uniform float uLiveCount;
  uniform vec3 uLivePole;
  uniform vec4 uLiveA[SUN_LIVE_MAX];
  uniform vec4 uLiveB[SUN_LIVE_MAX];
  uniform vec4 uLiveC[SUN_LIVE_MAX];

  vec3 sunLatLon(float lat, float lon) {
    float cl = cos(lat);
    return vec3(cl * cos(lon), sin(lat), cl * sin(lon));
  }

  // One spot at c: umbra radius rU (radians on the unit sphere), penumbra
  // round the fraction penFrac of its rim (0 for a pore). tone: the
  // continuum intensity of a big umbra's core and of the penumbra, relative
  // to the photosphere (SUN_SPOT_TONE for the modelled groups,
  // SUN_LIVE_TONE for NOAA's regions).
  void sunSpot(vec3 n, vec3 c, float rU, float penFrac, float seed, float footprint, float seconds, vec2 tone, inout SunSpots s) {
    if (rU < 0.0004) return;
    float hasPen = step(0.01, penFrac);
    float rP = rU * mix(1.0, 2.45, hasPen);
    vec3 dv = n - c;
    float reach = rP * 1.45 + footprint;
    if (dot(dv, dv) > reach * reach) return;
    vec3 e1 = normalize(cross(c, vec3(0.0, 1.0, 0.0)));
    vec3 e2 = cross(c, e1);
    vec2 q = vec2(dot(dv, e1), dot(dv, e2));
    float r = length(q);
    float az = atan(q.y, q.x + 1e-9);
    vec2 ca = vec2(cos(az), sin(az));

    // Outlines: lobed and ragged, a different one per spot.
    float umbW = 1.0 + 0.10 * sin(2.0 * az + seed * 3.1) + 0.06 * sin(3.0 * az - seed * 2.3)
               + 0.10 * snoise(vec3(ca * 1.6, seed * 7.0));
    float rUw = rU * umbW;
    float penW = 1.0 + 0.13 * sin(2.0 * az + seed * 1.7) + 0.07 * sin(5.0 * az - seed)
               + 0.12 * snoise(vec3(ca * 2.2, seed * 5.0 + 3.0));
    // The filaments' ends fray the outer edge.
    float fray = 1.0 - smoothstep(0.5, 1.0, footprint / (rP * 0.08));
    penW += 0.08 * fray * snoise(vec3(ca * 9.0, seed * 3.0 + 11.0));
    // A partial penumbra covers only one side of the rim.
    float side = cos(az - seed * 5.3) - (1.0 - 2.4 * penFrac);
    float rPw = mix(rUw * 1.04, rP * penW, smoothstep(-0.25, 0.25, side) * hasPen);
    if (r > rPw * 1.1 + footprint) return;

    float aaU = max(footprint, rU * 0.05);
    float aaP = max(footprint, rP * 0.03);
    float umb = 1.0 - smoothstep(rUw - aaU, rUw + aaU, r);
    float pen = 1.0 - smoothstep(rPw - aaP, rPw + aaP, r);

    // A light bridge across some of the bigger umbrae: a lane of small,
    // dim granules with a dark seam down its middle (SST, DKIST).
    float bridge = 0.0;
    float Ib = 0.0;
    if (rU > 0.009 && fract(seed * 7.13) > 0.45) {
      vec2 axis = vec2(cos(seed * 4.1), sin(seed * 4.1));
      vec2 qu = q / rU;
      float across = dot(qu, vec2(-axis.y, axis.x)) + 0.15 * snoise(vec3(qu * 1.5, seed));
      float halfW = 0.07 + 0.03 * snoise(vec3(qu * 3.0, seed + 2.0));
      float aaB = footprint / rU;
      bridge = 1.0 - smoothstep(halfW, halfW + 0.04 + aaB, abs(across));
      float kB = 1.0 - smoothstep(0.3, 0.7, footprint / (rU * 0.03));
      float cells = abs(snoise(vec3(qu * 22.0, seed * 3.0 + seconds * 0.004)));
      float seam = 1.0 - smoothstep(0.0, 0.012 + aaB, abs(across));
      Ib = 0.62 + kB * (0.22 * (smoothstep(0.05, 0.5, cells) - 0.6) - 0.18 * seam);
    }

    // The umbra: darkest at its heart, lighter toward its rim, and pores
    // never as dark as a full umbra.
    float ru = r / max(rUw, 1e-5);
    float heart = mix(max(0.42, tone.x + 0.25), tone.x, smoothstep(0.0015, 0.008, rU));
    float Iu = mix(heart, heart + 0.12, smoothstep(0.3, 1.0, ru));
    // Umbral dots: faint specks a few hundred km across that come and go,
    // commonest toward the umbra's rim.
    float dotCell = rU * 0.06;
    float kDot = 1.0 - smoothstep(0.5, 1.0, footprint / (dotCell * 0.3));
    if (kDot > 0.0 && umb > 0.0) {
      vec2 g = q / dotCell;
      vec2 gi = floor(g);
      vec3 hd = sunHash3(vec3(gi, seed * 31.0));
      float dd = length(g - gi - (0.25 + 0.5 * hd.xy));
      float size = 0.07 + 0.1 * fract(hd.z * 13.7);
      float flick = 0.5 + 0.5 * sin(seconds * 0.02 + hd.z * 6.2831853);
      float present = step(1.0 - mix(0.15, 0.45, smoothstep(0.3, 1.0, ru)), hd.z);
      float speck = present * (1.0 - smoothstep(size * 0.4, size, dd)) * flick;
      Iu += kDot * 0.2 * (speck - 0.01);
    }

    // The penumbra: radial filaments, the pattern streaming outward (the
    // Evershed flow) while bright grains at the inner heads drift in.
    float u = clamp((r - rUw) / max(rPw - rUw, 1e-5), 0.0, 1.0);
    float Ip = tone.y - 0.05 * u;
    float spacing = rP * 0.055;
    float kFil = 1.0 - smoothstep(0.35, 0.8, footprint / spacing);
    if (kFil > 0.0 && umb < 1.0) {
      float azT = az + 0.3 * u * sin(seed * 2.7);
      vec2 cs = vec2(cos(azT), sin(azT));
      float flow = seconds * 0.002;
      float ridge = 1.0 - abs(snoise(vec3(cs * 9.0, u * 0.8 - flow + seed)));
      float bundle = snoise(vec3(cs * 3.0, u * 0.4 - flow * 0.5 + seed * 1.3));
      float core = smoothstep(0.9, 0.98, ridge) * (1.0 - smoothstep(0.3, 0.7, footprint / (spacing * 0.35)));
      float grains = smoothstep(0.75, 0.95, ridge) * (1.0 - smoothstep(0.0, 0.3, u))
                   * (0.5 + 0.5 * snoise(vec3(cs * 9.0, u * 2.0 + flow * 2.0 + seed * 0.7)));
      Ip += kFil * (0.40 * (ridge * ridge - 0.55) + 0.06 * bundle - 0.16 * core + 0.18 * grains);
    }

    float Ic = mix(Ip, Iu, umb);
    Ic = mix(Ic, Ib, bridge * umb);
    float I = mix(1.0, Ic, pen);
    s.shade = min(s.shade, I);
    s.umbra = max(s.umbra, umb * (1.0 - bridge) * pen);
    s.cover = max(s.cover, pen);
  }

  // Today's regions: the same spots and plage as the model's groups, laid
  // out from NOAA's class, area and extent, and a flare's brightening as two
  // ribbons either side of the line between the polarities.
  void sunLiveRegions(vec3 n, float seconds, float footprint, inout SunSpots s) {
    for (int k = 0; k < SUN_LIVE_MAX; k++) {
      if (float(k) >= uLiveCount) break;
      vec4 A = uLiveA[k];
      vec4 B = uLiveB[k];
      vec4 C = uLiveC[k];
      vec3 c0 = A.xyz;
      float sep = B.x;
      float rLead = A.w;
      float reachRegion = sep * 0.9 + rLead * 3.6 + 0.06;
      vec3 dc = n - c0;
      if (dot(dc, dc) > reachRegion * reachRegion) continue;
      float hemi = dot(c0, uLivePole) >= 0.0 ? 1.0 : -1.0;
      // West (the way the Sun turns, where the leader leads) and north.
      vec3 west = normalize(cross(uLivePole, c0));
      vec3 north = normalize(uLivePole - c0 * dot(uLivePole, c0));
      // Joy's law: the leader nearer the equator.
      vec3 axis = cos(B.y) * west - sin(B.y) * hemi * north;
      vec3 cLead = normalize(c0 + axis * (0.5 * sep));
      vec3 cFoll = normalize(c0 - axis * (0.5 * sep));
      float seed = C.z;
      if (rLead > 0.0) {
        sunSpot(n, cLead, rLead, B.z, seed, footprint, seconds, uLiveTone, s);
        if (B.w >= 0.0) sunSpot(n, cFoll, C.x, B.w, seed + 1.7, footprint, seconds, uLiveTone, s);
        vec3 across = normalize(cross(cFoll - cLead + axis * 1e-4, c0));
        for (int m = 0; m < 3; m++) {
          if (float(m) >= C.y) break;
          vec3 hm = sunHash3(vec3(seed * 5.0 + float(m), 3.0, 37.0));
          vec3 cm = normalize(mix(cLead, cFoll, mix(0.25, 1.1, hm.x)) + across * (hm.y - 0.5) * 0.5 * max(sep, 0.02));
          float rU = rLead * mix(0.15, 0.35, hm.z);
          sunSpot(n, cm, rU, smoothstep(0.003, 0.0055, rU) * hm.z, seed + 3.1 + float(m), footprint, seconds, uLiveTone, s);
        }
      }
      vec3 ab = cFoll - cLead;
      float along = clamp(dot(n - cLead, ab) / max(dot(ab, ab), 1e-8), 0.0, 1.0);
      float dSeg = length(n - cLead - ab * along);
      s.plage = max(s.plage, 1.0 - smoothstep(0.25, 1.0, dSeg / (sep * 0.35 + rLead * 3.0 + 0.025)));
      if (C.w > 0.0) {
        // Two ribbons along the polarity inversion line, the centre of the
        // group, about a third of the separation apart.
        vec3 mid = normalize(c0);
        float offAxis = dot(n - mid, axis);
        float alongLine = dot(n - mid, normalize(cross(axis, mid)));
        float halfLen = 0.45 * max(sep, 0.03);
        float w = max(0.004, footprint * 1.5);
        float lineFade = 1.0 - smoothstep(halfLen * 0.6, halfLen, abs(alongLine));
        // Each ribbon bends and beads along its length, a little differently
        // on either side of the line.
        float side = sign(offAxis);
        float t = alongLine / halfLen;
        float bend = 0.05 * max(sep, 0.03) * (sin(t * 2.6 + seed + side) + 0.6 * snoise(vec3(t * 2.0, side, seed)));
        float beads = 0.55 + 0.45 * snoise(vec3(t * 7.0, side * 3.0, seed * 1.3));
        float dr = (abs(offAxis + bend) - 0.16 * max(sep, 0.03)) / w;
        float ribbons = beads * exp(-dr * dr);
        float kernel = exp(-dot(n - mid, n - mid) / pow(0.3 * max(sep, 0.03), 2.0));
        s.flare = max(s.flare, C.w * (lineFade * ribbons + 0.35 * kernel));
      }
    }
  }

  // Every spot over n, and the faculae round their groups. days: the
  // simulation's date in days from J2000; seconds: the surface clock.
  SunSpots sunSpots(vec3 n, float days, float seconds, float footprint) {
    SunSpots s;
    s.shade = 1.0;
    s.umbra = 0.0;
    s.cover = 0.0;
    s.plage = 0.0;
    s.flare = 0.0;
    if (uLiveSun > 0.5) {
      sunLiveRegions(n, seconds, footprint, s);
      return s;
    }
    float cyc = fract((days - SUN_CYCLE_START) / SUN_CYCLE_DAYS);
    // The cycle: a quick rise to its peak about four years in, then a
    // slow decline; the spots start near 28 degrees and end near 7.
    float cycleLevel = pow(sin(3.14159265 * pow(cyc, 0.7)), 2.0);
    float latMean = 28.0 * SUN_DEG * exp(-1.47 * cyc);
    for (int k = 0; k < SUN_REGION_SLOTS; k++) {
      float fk = float(k);
      vec3 hk = sunHash3(vec3(fk, 7.0, 3.0));
      float period = 24.0 + 16.0 * hk.x;
      float slot = days / period + hk.y;
      float gen = floor(slot);
      float ph = slot - gen;
      vec3 h = sunHash3(vec3(fk, gen, 11.0));
      vec3 h2 = sunHash3(vec3(fk, gen, 23.0));
      // Slot 0 always holds a group, so the disc is never bare, slot 1
      // most of the time, the rest as the cycle allows.
      float chance = k == 0 ? 1.0 : (k == 1 ? 0.45 + 0.55 * cycleLevel : 0.9 * cycleLevel);
      if (h2.z > chance) continue;

      float hemi = h.x < 0.5 ? -1.0 : 1.0;
      float lat = hemi * max(latMean + (h.y - 0.5) * 12.0 * SUN_DEG, 3.0 * SUN_DEG);
      float sl2 = sin(lat) * sin(lat);
      float drift = (14.713 - 2.396 * sl2 - 1.787 * sl2 * sl2 - SUN_MESH_RATE) * SUN_DEG;
      // The mesh turns toward lower lon here, so a faster belt drifts that way.
      float lon = h.z * 6.2831853 - drift * ph * period;
      // Most groups are modest and a few are big; slot 0's always is.
      float big = k == 0 ? mix(0.6, 1.0, h2.x) : pow(h2.x, 1.4);
      float rLead = mix(0.006, 0.027, big) * (0.8 + 0.2 * cycleLevel);
      float emerge = smoothstep(0.0, 0.1, ph);
      // The polarities move apart as the region emerges.
      float sep = (0.035 + 0.11 * big) * (0.5 + 0.5 * emerge);
      vec3 c0 = sunLatLon(lat, lon);
      float reachRegion = sep * 0.9 + rLead * 3.6 + 0.03;
      vec3 dc = n - c0;
      if (dot(dc, dc) > reachRegion * reachRegion) continue;

      // Joy's law: tilted about half the latitude, the leader (ahead in
      // the rotation) nearer the equator.
      float tilt = 0.5 * abs(lat) + (h2.y - 0.5) * 0.35;
      float dLat = 0.5 * sep * sin(tilt) * hemi;
      float dLon = 0.5 * sep * cos(tilt) / cos(lat);
      vec3 cLead = sunLatLon(lat - dLat, lon - dLon);
      vec3 cFoll = sunLatLon(lat + dLat, lon + dLon);

      float leadArea = emerge * clamp(1.0 - (ph - 0.1) / (k == 0 ? 0.9 : 0.75), 0.0, 1.0);
      float follArea = emerge * clamp(1.0 - (ph - 0.1) / 0.35, 0.0, 1.0);
      float seed = fk * 4.7 + gen * 1.31;

      float rU = rLead * sqrt(leadArea);
      sunSpot(n, cLead, rU, smoothstep(0.0028, 0.0045, rU), seed, footprint, seconds, SUN_SPOT_TONE, s);
      rU = rLead * (0.45 + 0.3 * h.y) * sqrt(follArea);
      sunSpot(n, cFoll, rU, smoothstep(0.003, 0.006, rU) * (0.45 + 0.55 * h2.y), seed + 1.7, footprint, seconds, SUN_SPOT_TONE, s);
      vec3 across = normalize(cross(cFoll - cLead, c0));
      for (int m = 0; m < 3; m++) {
        vec3 hm = sunHash3(vec3(fk * 5.0 + float(m), gen, 37.0));
        float smallArea = emerge * clamp(1.0 - (ph - 0.1) / (0.2 + 0.3 * hm.z), 0.0, 1.0);
        vec3 cm = normalize(mix(cLead, cFoll, mix(0.3, 1.25, hm.x)) + across * (hm.y - 0.5) * 0.5 * sep);
        rU = rLead * mix(0.12, 0.36, hm.z) * sqrt(smallArea);
        sunSpot(n, cm, rU, smoothstep(0.003, 0.0055, rU) * hm.z, seed + 3.1 + float(m), footprint, seconds, SUN_SPOT_TONE, s);
      }

      // Plage round the group, which outlives its spots.
      vec3 ab = cFoll - cLead;
      float along = clamp(dot(n - cLead, ab) / max(dot(ab, ab), 1e-8), 0.0, 1.0);
      float dSeg = length(n - cLead - ab * along);
      float plageLife = emerge * (1.0 - smoothstep(0.6, 1.0, ph));
      s.plage = max(s.plage, plageLife * (1.0 - smoothstep(0.25, 1.0, dSeg / (sep * 0.35 + rLead * 3.0 + 0.025))));
    }
    return s;
  }
`;

export const sunVertexShader = `
  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vPosition;
  varying vec3 vWorldPos;
  // World-space origin of the sun mesh. modelMatrix is only available
  // in the vertex shader so we compute the sun's world position here
  // and pass it via a varying for the fragment shader to use as the
  // anchor for distance-to-sun calculations. This stays correct under
  // the satellite-inspection origin-shift (where the entire scene is
  // translated to keep the focus body near 0,0,0 for float precision).
  varying vec3 vSunCenter;
  // The drawn radius in world units, so distances can be read in solar
  // radii in either scale mode and in the To-Scale lineup.
  varying float vSunRadius;
  varying vec3 vFragWorld;

  void main() {
    vUv = uv;
    vFragWorld = (modelMatrix * vec4(position, 1.0)).xyz;
    vNormal = normalize(normalMatrix * normal);
    vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
    vWorldPos = position;
    vSunCenter = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    vSunRadius = length(position) * length(modelMatrix[0].xyz);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const sunFragmentShader = `
  uniform sampler2D sunTexture;
  uniform float time;
  uniform float realisticSun;
  // Kept for the callers that write it; the far field is baked now.
  uniform float octaveLod;
  // The far field baked to cube maps (src/sunfarfield.js): the current
  // bake, the next one and how far the fade between them has gone.
  uniform samplerCube uFarCurr;
  uniform samplerCube uFarNext;
  uniform float uFarMix;
  uniform float uFarBaked;
  // The simulation's date in days from J2000, which the spot groups'
  // lives and drift follow.
  uniform float uSunDays;
  // A gain on the disc (the artistic Sun blown out white while it stands
  // for an overexposed point, updateLensFlare in main.js).
  uniform vec3 uSunTint;
  // The Earth's centre and drawn radius while a ray to the Sun passes
  // through its air (w = 0 otherwise, updateSunAirTint in main.js): each
  // pixel's ray dims and reddens with its own tangent height.
  uniform vec4 uEarthAir;
  varying vec3 vFragWorld;

  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vPosition;
  varying vec3 vWorldPos;
  varying vec3 vSunCenter;
  varying float vSunRadius;

  ${OKLAB_MIX_GLSL}

  ${SIMPLEX_NOISE_GLSL}

  ${SUN_FAR_FIELD_GLSL}

  ${SUN_GRANULATION_GLSL}

  ${SUN_SPOTS_GLSL}

  // The Realistic disc: the photosphere's colour from space, white (a
  // 5,772 K Sun is white to the eye; Andre asked for it fully white at both
  // scales on 1 Nov 2026), and the HDR level it is drawn at. The level sits
  // just above the bloom threshold so the tone curve keeps the limb
  // darkening (at the old 4.5 the curve flattened a 50% drop to 5%); the
  // glare round the disc carries the brightness instead (main.js).
  const vec3 SUN_WHITE = vec3(1.0);
  const float SUN_REALISTIC_GAIN = 2.4;
  const float SUN_FAR_RATE_GLSL = ${SUN_FAR_RATE.toFixed(6)};
  const float SUN_SURFACE_PACE_GLSL = ${SUN_SURFACE_PACE.toFixed(2)};
  const float SUN_CHURN_RATE_GLSL = ${SUN_CHURN_RATE.toFixed(4)};

  void main() {
    vec3 n = normalize(vWorldPos);
    vec3 viewDir = normalize(-vPosition);
    float cosAngle = max(dot(viewDir, normalize(vNormal)), 0.0);
    float surfaceSeconds = time * SUN_SURFACE_PACE_GLSL;

    // Distance from the camera in solar radii. vSunCenter is the mesh's
    // true world position, which holds under the satellite-inspection
    // origin shift. The close-up detail runs from 5 radii in and is full
    // inside 1.67, in either scale mode.
    float distR = length(cameraPosition - vSunCenter) / max(vSunRadius, 1e-6);
    float closeLod = 1.0 - smoothstep(1.67, 5.0, distR);
    // Realistic is the clean white disc from space, and up close its own
    // white granulation (below); Detailed turns to its coloured surface.
    float realisticBlend = realisticSun;

    // The pixel's footprint on the surface in solar radii, taken outside
    // any branch.
    float footprint = max(length(dFdx(n)), length(dFdy(n)));

    // === SUNSPOTS ===
    SunSpots spots = sunSpots(n, uSunDays, surfaceSeconds, footprint);

    // === FAR FIELD ===
    // The plasma churns: its lookup is carried by a slowly evolving smooth
    // displacement, two octaves, so the cells drift, stretch and fold
    // continuously between bakes (the realistic disc only reads its
    // facular activity, so it skips this).
    vec3 nFar = n;
    if (realisticBlend < 0.999) {
      float ct = time * SUN_CHURN_RATE_GLSL;
      vec3 w = 0.025 * vec3(
          snoise(n * 3.0 + vec3(0.0, 0.0, ct)),
          snoise(n * 3.0 + vec3(31.7, 0.0, ct)),
          snoise(n * 3.0 + vec3(0.0, 47.3, ct)))
        + 0.009 * vec3(
          snoise(n * 7.0 + vec3(13.1, 0.0, ct * 1.7)),
          snoise(n * 7.0 + vec3(0.0, 71.9, ct * 1.7)),
          snoise(n * 7.0 + vec3(5.3, 19.1, ct * 1.7)));
      nFar = normalize(n + w - n * dot(n, w));
    }
    vec4 far;
    if (uFarBaked > 0.5) {
      far = mix(textureCube(uFarCurr, nFar), textureCube(uFarNext, nFar), uFarMix);
    } else {
      float texLod = log2(max(footprint * float(textureSize(sunTexture, 0).x) / 6.28318530718, 1.0));
      far = sunFarField(nFar, time * SUN_FAR_RATE_GLSL, texLod);
    }
    vec3 detailed = far.rgb * SUN_FAR_SCALE;
    float activity = far.a;

    // Faculae: nearly invisible at disc centre and bright mottling toward
    // the limb, where the flux tubes' hot walls come into view (SDO/HMI).
    // Most of them are the plage round the active regions, broken into
    // patches on the network; the far field's activity adds the quiet
    // Sun's.
    float facularLimbVis = smoothstep(0.30, 0.85, 1.0 - cosAngle);
    float plageGrain = 0.0;
    if (spots.plage > 0.001) {
      float patches = smoothstep(-0.15, 0.55, snoise(n * 42.0 + vec3(3.3, 0.0, 9.1)));
      plageGrain = spots.plage * mix(patches, 0.45, smoothstep(0.004, 0.012, footprint));
    }
    detailed += vec3(1.0, 0.96, 0.85) * (activity * (0.03 + 0.35 * facularLimbVis) + plageGrain * 0.4 * facularLimbVis);

    // === GRANULATION (close) ===
    // granWhite is the surface's white-light brightness for the Realistic
    // style: granules and lanes at their true contrast, in white.
    float granWhite = 1.0;
    if (closeLod > 0.001) {
      SunGranulation gran = sunGranulation(n, surfaceSeconds, footprint);
      // Faculae on the network toward the limb, the flux tubes' hot walls:
      // granular bright patches, not lines, strongest in the plage. No
      // granules inside a spot.
      float granI = gran.intensity * (1.0 + (0.15 + 0.3 * spots.plage) * gran.plage * facularLimbVis);
      granI = mix(granI, 1.0, spots.cover);
      granWhite = granI;
      // Up close the surface is the granulation; the far field's plasma
      // keeps a tenth for its large-scale colour while the granules are
      // small, and very little once they resolve (magnified past its
      // cube's texels it would show as smooth swirls).
      detailed = oklchMix(detailed, sunGranuleColour(granI), closeLod * (0.9 + 0.08 * gran.resolved));
    }

    // === REALISTIC DISC ===
    // Smooth: no plasma, only faculae toward the limb and the spots (as
    // SDO/HMI's continuum shows the Sun).
    vec3 realistic = SUN_WHITE * mix(1.0 + facularLimbVis * (activity * 0.05 + plageGrain * 0.22), granWhite, closeLod);

    // The spots: a red-brown umbra (it is about 1,500 K cooler) inside a
    // warm grey penumbra.
    vec3 spotTint = mix(vec3(1.0), vec3(1.15, 0.97, 0.82), spots.umbra)
                  * mix(vec3(1.0), vec3(1.05, 0.99, 0.93), spots.cover * (1.0 - spots.umbra));
    vec3 spotShade = spots.shade * spotTint;
    detailed *= spotShade;
    realistic *= spotShade;
    // A flare's brightening at its region (live regions only): white light
    // from the ribbons, a little warm.
    vec3 flareLight = vec3(1.0, 0.97, 0.9) * spots.flare;
    detailed += flareLight;
    realistic += flareLight;

    // === LIMB DARKENING, Neckel & Labs per channel ===
    // Blue darkens more than red because blue continuum opacity is higher,
    // so blue sees shallower, cooler layers (Neckel & Labs 1994, Sol.
    // Phys. 153, 91): the warm, darker limb of every white-light image.
    float oneMinusMu = 1.0 - cosAngle;
    vec3 ldU = vec3(0.47, 0.53, 0.63);
    vec3 ldV = vec3(0.23, 0.18, 0.13);
    vec3 limbDarkening = vec3(1.0) - ldU * oneMinusMu - ldV * oneMinusMu * oneMinusMu;
    detailed *= limbDarkening;
    // The Realistic disc darkens by the same law's luminance, so its limb
    // dims without turning warm.
    realistic *= dot(limbDarkening, vec3(0.2126, 0.7152, 0.0722));

    // The two styles, mixed along the perceptual hue arc. No pink rim on
    // the disc itself: the chromosphere stands outside the limb
    // (sunChromosphereFragmentShader).
    vec3 baseColor = oklchMix(detailed, realistic, realisticBlend);

    // HDR output. Detailed stays at 2.2 so the plasma colours read through
    // bloom; Realistic sits just over the bloom threshold (see SUN_WHITE).
    baseColor *= mix(2.2, SUN_REALISTIC_GAIN, realisticBlend);

    vec3 air = vec3(1.0);
    if (uEarthAir.w > 0.0) {
      vec3 toFrag = vFragWorld - cameraPosition;
      float fragDist = length(toFrag);
      vec3 rayDir = toFrag / max(fragDist, 1e-9);
      vec3 toEarth = uEarthAir.xyz - cameraPosition;
      float tE = dot(toEarth, rayDir);
      if (tE > 0.0 && tE < fragDist) {
        float bE = sqrt(max(dot(toEarth, toEarth) - tE * tE, 0.0));
        float hKm = (bE / uEarthAir.w - 1.0) * 6371.0;
        if (hKm < 120.0) {
          // Chapman's grazing air mass and Rayleigh's optical depths, as
          // sunAirTransmission in main.js.
          air = exp(-vec3(0.049, 0.096, 0.214) * 70.7 * exp(-max(hKm, 0.0) / 8.0));
        }
      }
    }
    gl_FragColor = vec4(baseColor * uSunTint * air, 1.0);
  }
`;

// Bakes the far field into one band of one cube face (src/sunfarfield.js).
export const sunFarFieldBakeVertexShader = `
  void main() {
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

export const sunFarFieldBakeFragmentShader = `
  uniform sampler2D sunTexture;
  uniform float uFarTime;
  uniform int uFace;
  uniform float uSize;
  uniform float uTexLod;

  ${OKLAB_MIX_GLSL}

  ${SIMPLEX_NOISE_GLSL}

  ${SUN_FAR_FIELD_GLSL}

  // The direction a texel of a cube face looks along, in the GL cube map
  // convention (the same one textureCube reads with).
  vec3 cubeDirection(int face, vec2 st) {
    vec2 c = st * 2.0 - 1.0;
    if (face == 0) return vec3(1.0, -c.y, -c.x);
    if (face == 1) return vec3(-1.0, -c.y, c.x);
    if (face == 2) return vec3(c.x, 1.0, c.y);
    if (face == 3) return vec3(c.x, -1.0, -c.y);
    if (face == 4) return vec3(c.x, -c.y, 1.0);
    return vec3(-c.x, -c.y, -1.0);
  }

  void main() {
    vec3 n = normalize(cubeDirection(uFace, gl_FragCoord.xy / uSize));
    gl_FragColor = sunFarField(n, uFarTime, uTexLod);
  }
`;

export const coronaVertexShader = `
  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const coronaFragmentShader = `
  uniform float time;
  uniform float sunRadius;
  uniform vec3 coronaColor;

  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    vec3 viewDir = normalize(-vPosition);
    float fresnel = 1.0 - abs(dot(viewDir, vNormal));
    float glow = pow(fresnel, 3.0) * 0.5;
    vec3 color = coronaColor;
    gl_FragColor = vec4(color, glow);
  }
`;

export const solarWindVertexShader = `
  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const solarWindFragmentShader = `
  uniform float time;
  uniform vec3 glowColor;

  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    vec3 viewDir = normalize(-vPosition);
    float fresnel = 1.0 - abs(dot(viewDir, vNormal));
    float glow = pow(fresnel, 4.0);
    vec3 color = glowColor;
    float alpha = glow * 0.2;
    gl_FragColor = vec4(color, alpha);
  }
`;

// Solar prominences as volumes of glowing plasma. Each one is a box in
// its own frame (solar radii, the Sun's centre at the origin, the base
// on +Y, the spine along X), marched front to back. What a camera sees
// of a prominence is H-alpha line emission with a roughly constant
// source function, so the brightness of a ray is S (1 - e^-tau): faint
// threads add up, dense cores saturate instead of blowing out, and the
// colour runs from deep crimson where the plasma is thin to the pink
// and pink-white of eclipse photographs where it is thick. In front of
// the disc the same plasma only absorbs a little (a filament), because
// the photosphere outshines it.
//
// The fine structure is field-aligned threads: ridged noise stretched
// along the field, 2,500 to 3,000 km apart, with each octave replaced by
// its mean once it falls under two pixels so nothing shimmers at range.
// Three shapes, picked by PROM_KIND:
//   0 quiescent hedgerow: a thin curtain of vertical threads standing on
//     barbs, arched gaps between the feet, a ragged crown, knots that
//     drain down the threads
//   1 post-flare loop arcade: nested loops of different sizes, coronal
//     rain sliding from the tops down both legs
//   2 flux-rope arch: one thick twisted rope, threads wound round it,
//     plasma siphoning foot to foot; uLift raises and swells it into an
//     eruption that lets go of its legs
const PROMINENCE_NOISE_GLSL = `
  float promHash(vec3 p) {
    p = fract(p * 0.1031);
    p += dot(p, p.zyx + 31.32);
    return fract((p.x + p.y) * p.z);
  }

  ${SIMPLEX_NOISE_GLSL}

  float promNoise(vec3 x) {
    vec3 i = floor(x);
    vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(mix(promHash(i), promHash(i + vec3(1.0, 0.0, 0.0)), f.x),
          mix(promHash(i + vec3(0.0, 1.0, 0.0)), promHash(i + vec3(1.0, 1.0, 0.0)), f.x), f.y),
      mix(mix(promHash(i + vec3(0.0, 0.0, 1.0)), promHash(i + vec3(1.0, 0.0, 1.0)), f.x),
          mix(promHash(i + vec3(0.0, 1.0, 1.0)), promHash(i + vec3(1.0, 1.0, 1.0)), f.x), f.y),
      f.z);
  }

  // Field-aligned threads. q is in thread units: x and z across the
  // field (one unit = one thread spacing), y along it. fp is the pixel
  // footprint in the same units; an octave fades to its mean as it
  // drops under about two pixels.
  float promThreads(vec3 q, float fp) {
    float sum = 0.0;
    float norm = 0.0;
    float amp = 1.0;
    for (int o = 0; o < 3; o++) {
      // Ridged gradient noise: its zero set, drawn out along the field,
      // makes long unbroken filaments with no lattice spacing. Mean of
      // (1 - |n|)^6 is 0.255.
      float thread = 1.0 - abs(snoise(q));
      thread *= thread;
      thread *= thread * thread;
      sum += amp * mix(0.255, thread, 1.0 - smoothstep(0.22, 0.55, fp));
      norm += amp;
      q = q * vec3(2.0, 1.5, 2.0) + vec3(19.1, 7.3, 3.9);
      fp *= 2.0;
      amp *= 0.7;
    }
    return sum / norm;
  }

  // Blobs (knots, rain) with the same footprint fade; 0.17 is the mean
  // of the 0.55-0.85 cut every caller uses.
  float promKnots(vec3 q, float fp) {
    float k = smoothstep(0.55, 0.85, promNoise(q));
    return mix(0.17, k, 1.0 - smoothstep(0.3, 0.7, fp));
  }
`;

export const sunProminenceVertexShader = `
  varying vec3 vLocal;

  void main() {
    vLocal = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const sunProminenceFragmentShader = `
  uniform float uTime;
  uniform float uIntensity;
  uniform float uLift;
  uniform float uSeed;
  uniform float uOpacity;
  uniform float uEmit;
  uniform float uPixelAngle;
  uniform vec4 uShape;
  uniform vec3 uBoxMin;
  uniform vec3 uBoxMax;
  uniform vec3 uCamLocal;
  uniform vec3 uFaintColor;
  uniform vec3 uMidColor;
  uniform vec3 uHotColor;

  varying vec3 vLocal;

  ${OKLAB_MIX_GLSL}
  ${PROMINENCE_NOISE_GLSL}

  const float PI = 3.14159265;

  float promDensity(vec3 p, float fp) {
    float r = length(p);
    float h = r - 1.0;
    if (h < 0.0) return 0.0;
    vec3 n = p / r;

  #if PROM_KIND == 0
    // Quiescent hedgerow. uShape: half length, height, half thickness, feet.
    float L = uShape.x;
    float H = uShape.y;
    float T = uShape.z;
    float u = n.x / L;
    float au = abs(u);
    if (au > 1.15 || h > H * 1.4) return 0.0;
    float hn = h / H;
    // The curtain bends gently along its length and leans with height,
    // and fans out toward the crown.
    float zc = T * (1.6 * sin(u * 1.9 + uSeed * 6.0) + 0.7 * sin(u * 4.7 + uSeed * 11.0))
      + h * 0.12 * sin(uSeed * 9.0);
    float thick = T * (0.6 + 1.4 * hn);
    float dz = (n.z - zc) / thick;
    if (abs(dz) > 2.6) return 0.0;
    // Crown: a dome with a ragged edge that changes over minutes.
    float crown = promNoise(vec3(u * 6.0 + uSeed * 17.0, uTime * 0.004, 0.5)) * 0.65
      + promNoise(vec3(u * 17.0, uTime * 0.009, uSeed * 5.0)) * 0.35;
    float top = H * sqrt(max(1.0 - au * au, 0.0)) * (0.6 + 0.55 * crown);
    // Barbs: narrow trunks down to the chromosphere that widen as they
    // rise into the curtain, low arches of empty corona between them.
    float ub = u + 0.06 * sin(hn * 3.0 + uSeed * 8.0);
    float gap = pow(0.5 - 0.5 * cos((ub + uSeed) * PI * uShape.w), 0.9);
    float bottom = H * 0.22 * gap * (1.0 - smoothstep(0.7, 1.0, au));
    float env = smoothstep(bottom, bottom + H * 0.12, h)
      * (1.0 - smoothstep(top * 0.5, top, h))
      * (1.0 - smoothstep(0.8, 1.1, au));
    if (env <= 0.0) return 0.0;
    // An irregular, domain-warped outline in the curtain's plane: lobes,
    // holes and dark bubbles rather than a clean dome.
    vec2 wp = vec2(u * 2.2, hn * 1.7);
    vec2 warp = vec2(
      promNoise(vec3(wp * 1.3, uSeed * 7.0 + uTime * 0.003)),
      promNoise(vec3(wp * 1.3 + 5.2, uSeed * 3.0 + uTime * 0.003))
    ) - 0.5;
    vec2 cw = (wp + warp * 0.9) * 2.0;
    float cloud = promNoise(vec3(cw, uSeed * 11.0)) * 0.6
      + promNoise(vec3(cw * 2.3, uSeed * 2.0 + uTime * 0.004)) * 0.4;
    env *= mix(0.2, 1.0, smoothstep(0.26, 0.58, cloud));
    float lambda = 0.004;
    float fpu = fp / lambda;
    // Vertical threads, coherent through the curtain's thickness so they
    // survive being seen through it, swaying a little and sliding slowly
    // down.
    float sway = 0.3 * sin(h * 70.0 + uTime * 0.05 + uSeed * 4.0) + warp.x * 2.0;
    vec3 q = vec3(n.x / lambda + sway, h / (lambda * 24.0) + uTime * 0.01, n.z / lambda * 0.18 + uSeed * 31.0);
    float threads = promThreads(q, fpu);
    float knots = promKnots(vec3(n.x / lambda, (h + uTime * 0.0009) / (lambda * 2.0), uSeed * 7.0), fpu);
    return env * exp(-dz * dz) * (0.45 + 1.5 * threads) * (0.85 + 0.45 * knots);

  #elif PROM_KIND == 1
    // Loop arcade. uShape: footpoint half-span, height, loop thickness, half depth.
    float a = uShape.x;
    float b = uShape.y;
    float w = uShape.z;
    float zz = n.z / uShape.w;
    if (abs(zz) > 1.25) return 0.0;
    // Separate loops along the arcade, each its own size and brightness,
    // with gaps of corona between them.
    float loopCell = n.z / 0.007 + uSeed * 5.0;
    float loops = smoothstep(0.35, 0.75, promNoise(vec3(loopCell, 0.5, uSeed * 3.0)));
    if (loops <= 0.0) return 0.0;
    float sz = 1.0 + 0.45 * (promNoise(vec3(loopCell * 0.5, 3.1, uSeed)) - 0.5) + 0.12 * zz;
    vec2 q2 = vec2(n.x / (a * sz), h / (b * sz));
    float rq = length(q2);
    float dr = (rq - 1.0) * 0.5 * (a + b) * sz;
    float shell = exp(-(dr * dr) / (w * w));
    if (shell < 0.01) return 0.0;
    // 0 at the feet, pi/2 at the loop top.
    float ang = atan(q2.y, abs(q2.x));
    float zenv = 1.0 - smoothstep(0.35, 1.2, abs(zz));
    float lambda = 0.0035;
    float fpu = fp / lambda;
    float threads = promThreads(vec3(dr / lambda, ang * 2.0, n.z / lambda + uSeed * 13.0), fpu);
    // Coronal rain: condensations sliding from the tops down both legs.
    float rain = promKnots(vec3(dr / lambda * 0.7, (PI * 0.5 - ang) * 9.0 - uTime * 0.05, n.z / lambda * 0.7), fpu);
    // The legs thin into the chromosphere; the tops carry the most plasma.
    float legs = 0.5 + 0.5 * smoothstep(0.1, 1.2, ang);
    // Post-flare loops are dense: several times a quiescent curtain's
    // column per unit length.
    return 2.5 * shell * loops * zenv * legs * (0.3 + 1.4 * threads) * (0.6 + 1.1 * rain);

  #else
    // Flux-rope arch. uShape: footpoint half-span, height, rope radius, twist (radians foot to foot).
    float a = uShape.x * (1.0 + uLift * 0.4);
    float b = uShape.y * (1.0 + uLift * 3.0);
    float Rt = uShape.z * (1.0 + uLift * 2.5);
    vec2 q2 = vec2(n.x / a, h / b);
    float rq = length(q2);
    // 0 at the +X foot, pi at the -X foot.
    float ang = atan(q2.y, q2.x);
    float sa = sin(ang);
    float u1 = (rq - 1.0) * mix(a, b, sa);
    // The rope writhes a little out of its plane.
    float v1 = n.z - Rt * 1.5 * sin(ang * 2.0 + uSeed * 5.0) * sa;
    // Thin legs, swollen apex, and a cross-section that bulges and pinches
    // along the rope.
    float tube = Rt * (0.6 + 0.7 * sa) * (0.8 + 0.4 * promNoise(vec3(ang * 4.0, uSeed * 9.0, uTime * 0.004)));
    vec2 cs = vec2(u1, v1) / tube;
    float rho2 = dot(cs, cs);
    if (rho2 > 2.2) return 0.0;
    float body = exp(-rho2 * 1.3);
    // An erupting rope lets go of its legs as it lifts.
    body *= mix(1.0, smoothstep(0.15, 0.7, sa), uLift);
    // Threads wound round the rope, slowly turning.
    float tw = uShape.w * ang / PI + uTime * 0.015;
    float c = cos(tw);
    float s = sin(tw);
    cs = vec2(c * cs.x - s * cs.y, s * cs.x + c * cs.y);
    float lambda = 0.0045 * (1.0 + uLift * 2.5);
    float fpu = fp / lambda;
    vec3 tq = vec3(cs.x * tube / lambda, ang * 2.5, cs.y * tube / lambda + uSeed * 23.0);
    float threads = promThreads(tq, fpu);
    // Strands: a few bundles of threads with darker lanes between.
    float strands = smoothstep(0.25, 0.7, promNoise(vec3(tq.x * 0.35, ang * 0.8, tq.z * 0.35)));
    // Plasma siphoning along the rope from one foot to the other.
    float flow = promKnots(vec3(tq.x * 0.7, ang * 7.0 - uTime * 0.04, tq.z * 0.7), fpu);
    return body * (0.2 + 0.8 * strands) * (0.25 + 1.4 * threads) * (0.7 + 0.8 * flow);
  #endif
  }

  void main() {
    vec3 ro = uCamLocal;
    vec3 rd = normalize(vLocal - ro);

    // The prominence's current bounds (the mesh is the largest they get).
    vec3 safeRd = mix(rd, vec3(1e-6), step(abs(rd), vec3(1e-6)));
    vec3 inv = 1.0 / safeRd;
    vec3 ta = (uBoxMin - ro) * inv;
    vec3 tb = (uBoxMax - ro) * inv;
    vec3 tmin = min(ta, tb);
    vec3 tmax = max(ta, tb);
    float t0 = max(max(max(tmin.x, tmin.y), tmin.z), 0.0);
    float t1 = min(min(tmax.x, tmax.y), tmax.z);

    // Stop at the photosphere, and note whether the disc is behind the
    // ray at all (a prominence high over the disc leaves its box first).
    float onDisc = 0.0;
    float pb = dot(ro, rd);
    float pc = dot(ro, ro) - 1.0;
    float disc = pb * pb - pc;
    if (disc > 0.0) {
      float tSun = -pb - sqrt(disc);
      if (tSun > 0.0) {
        t1 = min(t1, tSun);
        onDisc = 1.0;
      }
    }
    if (t1 <= t0) discard;

    float dt = (t1 - t0) / float(PROM_STEPS);
    // Per-pixel start offset (interleaved gradient noise) turns step
    // banding into fine grain.
    float jitter = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
    float column = 0.0;
    for (int i = 0; i < PROM_STEPS; i++) {
      float t = t0 + (float(i) + jitter) * dt;
      column += promDensity(ro + rd * t, t * uPixelAngle) * dt;
    }

    float tau = column * uOpacity;
    float glow = 1.0 - exp(-tau);
    if (glow < 0.002) discard;

    // Thin plasma is H-alpha crimson; thick plasma reads pink, and the
    // densest cores pink-white, as in eclipse photographs.
    vec3 color = oklchMix(uFaintColor, uMidColor, smoothstep(0.05, 0.6, glow));
    color = oklchMix(color, uHotColor, smoothstep(1.2, 4.0, tau));

    float strength = uIntensity * glow;
    vec3 emission = color * strength * uEmit * mix(1.0, 0.03, onDisc);
    // Premultiplied: off the limb pure emission, on the disc a faint
    // dark filament where the plasma absorbs the photosphere behind it.
    gl_FragColor = vec4(emission, onDisc * strength * 0.35);
  }
`;

// The chromosphere seen edge-on: a thin pink layer just off the limb
// under a forest of spicules, the jets several thousand km tall that make
// the limb look like burning grass in H-alpha. A shell slightly larger
// than the Sun; each pixel takes its ray's closest approach to the
// centre, so the layer's brightness is its column along a grazing ray
// (Chapman: exp(-h / H)) and the spicules are radial streaks keyed on
// where round the limb that point is. The disc itself keeps the sun
// shader's own chromospheric rim.
export const sunChromosphereFragmentShader = `
  uniform float uTime;
  uniform float uIntensity;
  uniform float uPixelAngle;
  uniform vec3 uCamLocal;
  uniform vec3 uFaintColor;
  uniform vec3 uMidColor;

  varying vec3 vLocal;

  ${OKLAB_MIX_GLSL}
  ${PROMINENCE_NOISE_GLSL}

  void main() {
    vec3 ro = uCamLocal;
    vec3 rd = normalize(vLocal - ro);
    float tca = -dot(ro, rd);
    if (tca <= 0.0) discard;
    vec3 pca = ro + rd * tca;
    float dmin = length(pca);
    float h = dmin - 1.0;
    if (h <= 0.0 || h > 0.02) discard;
    vec3 dir = pca / dmin;
    float fp = tca * uPixelAngle;

    float lambda = 0.0016;
    float fpu = fp / lambda;
    // Spicules: radial streaks round the limb, each reaching its own
    // height, rising and falling over a few minutes.
    float spic = promThreads(vec3(dir.x / lambda, dir.y / lambda, dir.z / lambda), fpu * 0.6);
    float reach = mix(0.004, 0.012, promNoise(dir / (lambda * 2.5) + vec3(0.0, uTime * 0.006, 0.0)));
    float jets = spic * (1.0 - smoothstep(reach * 0.35, reach, h));
    // The layer under them, ~1,100 km scale height.
    float layer = exp(-h / 0.0016);
    float tau = 1.6 * layer + 2.4 * jets;
    float glow = 1.0 - exp(-tau);
    if (glow < 0.003) discard;
    vec3 color = oklchMix(uFaintColor, uMidColor, smoothstep(0.1, 0.8, glow));
    // Additive blending scales by alpha.
    gl_FragColor = vec4(color * glow * uIntensity, 1.0);
  }
`;
